import {
  HttpClient,
  HttpBackend,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  finalize,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { environment } from 'src/config/environments/environment';

import { AuthStateService } from '../services/auth-state.service';
import { ApiErrorHandlerService } from '../services/handlers/api-error.service';
import JWTTokenService from '../services/jwt-token.service';
import { LoggerService } from '../services/logger.service';
import NotifierService from '../services/toaster.service';

type HttpBodyRequest = {
  url: string;
  withCredentials?: boolean;
  setHeaders?: {
    'Content-Type'?: string;
    'X-Company-Id'?: string;
  };
};

/**
 * HTTP Interceptor for backoffice API requests
 * Uses httpOnly cookies for authentication instead of Bearer tokens
 */
@Injectable()
export class ApiHttpInterceptor implements HttpInterceptor {
  private readonly env = environment;
  // '/security/log' : le journal ne doit jamais déclencher de rafraîchissement,
  // sous peine de réamorcer l'échec qu'il est en train de rapporter. Le service
  // poste désormais hors chaîne d'intercepteurs, ceci reste une seconde barrière.
  private readonly excludedUrls = [
    '/login',
    '/register',
    '/token/refresh',
    '/security/log',
  ];
  private readonly _httpClient: HttpClient;

  constructor(
    private readonly _injector: Injector,
    private readonly _handler: HttpBackend,
    private readonly _logger: LoggerService,
  ) {
    this._httpClient = new HttpClient(this._handler);
  }

  // Helper getters for lazy injection
  private get _tokenService(): JWTTokenService {
    return this._injector.get(JWTTokenService);
  }

  private get _router(): Router {
    return this._injector.get(Router);
  }

  private get _authState(): AuthStateService {
    return this._injector.get(AuthStateService);
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Simplified exclusion logic
    if (
      req.url.includes('/assets') ||
      req.url.includes('assets') ||
      req.url.endsWith('.json')
    ) {
      return next.handle(req);
    }

    // But allow requests to our own API (even if absolute URL)
    const isAbsolute =
      req.url.startsWith('http://') || req.url.startsWith('https://');

    // env.server !== '' est indispensable, pas défensif : en développement la
    // base est vide, l'API étant appelée en URL relative derrière le mandataire
    // nginx. Or toute chaîne commence par '' — sans ce garde, startsWith('')
    // rendait vrai pour N'IMPORTE QUELLE URL absolue, y compris un domaine
    // tiers, qui recevait alors withCredentials: true et donc le cookie de
    // session. Le test « should not intercept external HTTP requests » l'a
    // attrapé.
    const isOwnApi =
      isAbsolute &&
      this.env.server !== '' &&
      req.url.startsWith(this.env.server);

    if (isAbsolute && !isOwnApi) {
      return next.handle(req);
    }

    // Determine API URL
    let targetUrl = req.url;

    // If not absolute, prepend API URL
    if (!isAbsolute) {
      let urlWithoutAPI = false;
      if (req.url.startsWith('/_auth')) {
        urlWithoutAPI = true;
      }

      const prefix = urlWithoutAPI ? this.env.server : this.env.api;
      if (req.url.startsWith(prefix)) {
        targetUrl = req.url;
      } else {
        targetUrl = prefix + (req.url.startsWith('/') ? '' : '/') + req.url;
      }
    }

    const requestJSON: HttpBodyRequest = {
      url: targetUrl,
      // CRITICAL: Enable credentials for httpOnly cookies
      withCredentials: true,
    };

    // Set Content-Type header if needed
    if (!(req.body instanceof FormData)) {
      const contentType =
        req.headers?.get('Content-type') ?? 'application/ld+json';
      requestJSON['setHeaders'] = {
        'Content-Type': contentType,
      };
    }

    // Override Content-Type for PATCH requests
    if (req.method === 'PATCH') {
      const contentType =
        req.headers?.get('Content-type') ?? 'application/merge-patch+json';
      if (!requestJSON['setHeaders']) {
        requestJSON['setHeaders'] = {
          'Content-Type': contentType,
        };
      } else {
        requestJSON['setHeaders']['Content-Type'] = contentType;
      }
    }

    // Inject X-Company-Id header for tenant isolation (RLS)
    const companyId =
      typeof this._authState.getCurrentCompanyId === 'function'
        ? this._authState.getCurrentCompanyId()
        : null;
    if (companyId !== null) {
      requestJSON['setHeaders'] = {
        ...requestJSON['setHeaders'],
        'X-Company-Id': String(companyId),
      };
    }

    const authReq = req.clone({
      ...requestJSON,
      params: req.params,
    });

    // Send cloned request with credentials to the next handler
    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        // Log HTTP error
        this._logger.error('HTTP Error', err, {
          url: req.url,
          method: req.method,
          status: err.status,
        });

        // Handle 401 with Token Refresh first
        if (err.status === 401 && this.shouldAttemptRefresh(req)) {
          return this.handle401Error(requestJSON, req, next);
        }

        // Handle generic 401 Unauthorized (if refresh not attempted/applicable)
        if (err.status === 401) {
          this._logger.security('Unauthorized request detected', {
            url: req.url,
            status: 401,
          });

          // Clear auth state
          this._tokenService.clear();
          this._authState.clearAuthState();

          // Redirect to login
          this._router.navigateByUrl('/login');

          return throwError(() => err);
        }

        // Handle 403 Forbidden
        if (err.status === 403) {
          this._logger.security('Access forbidden - insufficient permissions', {
            url: req.url,
            method: req.method,
          });

          // Error handler will display appropriate message
          return throwError(() => err);
        }

        // Handle other errors
        const errorHandler = this._injector.get(ApiErrorHandlerService);
        errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Determine if we should attempt to refresh the token.
   *
   * The JWT and refresh token both live in httpOnly cookies sent
   * automatically via withCredentials — there is nothing readable in
   * localStorage to gate this on (a normal login never populates
   * 'keepConnection'/'refreshToken' there; only the legacy auto-connect
   * query-string flow in login.component.ts does). Gating refresh on those
   * meant it silently never fired for a standard login: any 401 sent the
   * user straight back to /login instead of attempting a silent refresh.
   */
  private shouldAttemptRefresh(request: HttpRequest<unknown>): boolean {
    return !this.excludedUrls.some((url) => request.url.includes(url));
  }

  /**
   * Handle 401 error by attempting a token refresh via the httpOnly
   * refresh cookie.
   */
  private handle401Error(
    requestJSON: HttpBodyRequest,
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.refreshConnection(requestJSON, req, next);
  }

  /**
   * Redirect to login and clear authentication state
   */
  redirectToLogin() {
    const notifier = this._injector.get(NotifierService);
    this._tokenService.setToken(null);
    this._authState.clearAuthState();
    this._router.navigateByUrl('/login');
    notifier.notify('error', 'error.disconnected');
  }

  /**
   * Ask the backend to mint a new JWT from the httpOnly refresh cookie,
   * then retry the original request. The new cookies are set directly by
   * the backend (JwtCookieEventSubscriber) — there is no token to read
   * from the response body.
   */
  /**
   * Appel de rafraîchissement en cours, partagé entre toutes les requêtes qui
   * se heurtent à un 401 en même temps.
   *
   * Indispensable depuis que l'API émet des jetons à usage unique
   * (single_use dans gesdinet_jwt_refresh_token.yaml) : sans ce partage, deux
   * requêtes tombant simultanément en 401 déclenchaient deux rafraîchissements,
   * le premier consommait le jeton et le second recevait un 401 — donc une
   * déconnexion, sur une page qui n'avait rien fait d'anormal.
   */
  private rafraichissementEnCours$: Observable<unknown> | null = null;

  /**
   * Demande un nouveau JWT à partir du cookie de rafraîchissement httpOnly.
   *
   * Aucun jeton n'est transmis dans le corps ni relu de la réponse : depuis la
   * migration vers les cookies httpOnly, l'API ne les renvoie plus et les pose
   * elle-même (JwtCookieEventSubscriber). Le corps vide est délibéré.
   */
  private rafraichir(): Observable<unknown> {
    if (this.rafraichissementEnCours$) {
      return this.rafraichissementEnCours$;
    }

    this.rafraichissementEnCours$ = this._httpClient
      .post(
        `${this.env.api}/token/refresh`,
        {},
        {
          // CRITICAL: Include credentials for refresh request
          withCredentials: true,
        },
      )
      .pipe(
        // finalize AVANT shareReplay : il doit s'exécuter une seule fois, à la
        // fin de l'appel réseau, et non à chaque désabonnement.
        finalize(() => {
          this.rafraichissementEnCours$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.rafraichissementEnCours$;
  }

  refreshConnection(
    requestJSON: HttpBodyRequest,
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ) {
    // L'appel de rafraîchissement est mutualisé ; seule la reprise de la
    // requête d'origine est propre à chaque appelant.
    return this.rafraichir().pipe(
      switchMap(() => {
        const authReq = req.clone({
          ...requestJSON,
          params: req.params,
        });
        return next.handle(authReq);
      }),
      catchError((err: HttpErrorResponse) => {
        this.redirectToLogin();
        return throwError(() => err);
      }),
    );
  }
}
